import { dateWords, type YearRule } from '../../days.ts';

/**
 * A gate done says who (design study 09, gates A, as Jason answered it, 2026-10-06: "either it is something that
 * happens like a retainer is received and its marked by me on my end as complete or it requires a sign off ... and
 * approved by xyz when done"): approved by the one who approves it, or done, marked by NSB. One rule for NSB's tile, its
 * panel and the client's drawing, read from the record its passing wrote (the API's `gatesPassedBy`), never guessed.
 */
export interface Passed {
  approved: boolean;
  /** Their name (NSB's reading) or first name (the client's); null when there is none to give. */
  by: string | null;
  /** It was the one reading (the client's drawing). */
  you?: boolean;
  /** The client's approval given outside the portal, recorded by NSB (L-16 A): approved by the client, NSB named on its panel alone. */
  recorded?: boolean;
}

/** A person's first name, as a tile has room for it. */
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/**
 * A gate done, in words. On a tile or a card: "Approved by Ann · Oct 9", "Approved by you · Oct 9", or "Done · Oct 9",
 * the day with its year where a tile's day carries it (`dateWords` `dayOrDate`, L-25a: "Approved by Ann · Nov 30,
 * 2008"); who marked it done is left to the panel, which has the room (`marked`): "Done · October 9, 2026, marked by
 * Jason". `long` writes the day in full, as the panel and a step's list do. With no record of who, it says what and when
 * alone. A tile with one line to give keeps the day and leaves who to the panel (look.ts `footOneLine`, L-25e).
 */
export function gateDone(p: Passed | null | undefined, on: string | null, o: { long?: boolean; marked?: boolean } & YearRule = {}): string {
  const day = on ? (o.long ? dateWords(on, 'date') : dateWords(on, 'dayOrDate', o)) : '';
  const when = day ? ` · ${day}` : '';
  if (p?.approved) return `Approved${p.you ? ' by you' : p.recorded ? ' by the client' : p.by ? ` by ${firstName(p.by)}` : ''}${when}`;
  return `Done${when}${o.marked && p?.by ? `, marked by ${firstName(p.by)}` : ''}`;
}
